import type { DictationItem } from '../learn/review/types';

/**
 * 英文單字庫(localStorage,純前端存)。
 *
 * 老闆/家長貼入單字 → 存 localStorage → 之後可轉成聽寫題 (`DictationItem`) 跑
 * 聽寫考試。學習/遊戲分離鐵則:這只是學習側單字資料,絕不寫入 commanderXp /
 * factionXP / 戰鬥數值。
 *
 * 存取慣例對齊 `src/shared/progress.ts`:單一 localStorage key、JSON 序列化、
 * load 容錯回 fallback(空陣列)而非丟例外。
 */

/** 一筆單字:英文 + 中文,可選知識點標籤。 */
export interface VocabWord {
  word: string;
  zh: string;
  kp?: string;
}

const STORAGE_KEY = 'eternal-fortress.vocab';

/**
 * 讀取單字庫。key 不存在或 JSON 壞掉時回空陣列(對齊 progress.loadProgress
 * 的 try/catch 容錯)。同時過濾掉缺 `word` 的髒資料。
 */
export function loadVocab(): VocabWord[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (v): v is VocabWord =>
        typeof v === 'object' &&
        v !== null &&
        typeof (v as { word?: unknown }).word === 'string' &&
        (v as { word: string }).word.length > 0,
    );
  } catch {
    return [];
  }
}

/** 覆寫整個單字庫。 */
export function saveVocab(list: VocabWord[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

/**
 * 批次加入單字並去重(by `word` 小寫)。既有的單字以後到者覆寫(更新 zh/kp),
 * 順序維持「既有在前、新單字依輸入序接在後」。
 */
export function addVocabBatch(words: VocabWord[]): void {
  const existing = loadVocab();
  const byKey = new Map<string, VocabWord>();
  for (const w of existing) {
    byKey.set(w.word.toLowerCase(), w);
  }
  for (const w of words) {
    if (typeof w.word !== 'string' || w.word.length === 0) continue;
    byKey.set(w.word.toLowerCase(), w);
  }
  saveVocab([...byKey.values()]);
}

/**
 * 轉成聽寫題清單。`idx` 從 1 起算,`word` 為要拼的英文,`hintZh` 帶中文提示。
 */
export function toDictationItems(list: VocabWord[]): DictationItem[] {
  return list.map((w, i) => ({
    idx: i + 1,
    word: w.word,
    hintZh: w.zh,
  }));
}
